import {
  getTabLabel,
  hasLegacySubTabQuery,
  parseTabId,
  resolveTabId,
  type TabItem,
} from "@/utils/tabQuery";

export type PropertyTabItem = TabItem;

export const PROPERTY_DETAIL_TABS = [
  { id: 0, label: "概要" },
  { id: 1, label: "レントロール" },
  { id: 2, label: "予実" },
  { id: 3, label: "点検/清掃" },
  { id: 4, label: "工事" },
  { id: 5, label: "収支" },
] as const satisfies readonly PropertyTabItem[];

export const PROPERTY_DETAIL_TAB_IDS = {
  OVERVIEW: 0,
  RENTROLL: 1,
  BUDGET: 2,
  INSPECTION: 3,
  REPAIR: 4,
  MANAGEMENT: 5,
} as const;

export const PROPERTY_OVERVIEW_SUBTABS = [
  { id: 0, label: "物件構成" },
  { id: 1, label: "詳細情報" },
  { id: 2, label: "書類" },
] as const satisfies readonly PropertyTabItem[];

export const PROPERTY_OVERVIEW_SUBTAB_IDS = {
  COMPOSITION: 0,
  DETAILS: 1,
  DOCUMENTS: 2,
} as const;

export const EXPENSE_MANAGEMENT_SUBTABS = [
  { id: 0, label: "支出" },
  { id: 1, label: "収入" },
] as const satisfies readonly PropertyTabItem[];

export const DEFAULT_PROPERTY_DETAIL_TAB_ID =
  PROPERTY_DETAIL_TAB_IDS.OVERVIEW;
export const DEFAULT_PROPERTY_OVERVIEW_SUBTAB_ID =
  PROPERTY_OVERVIEW_SUBTAB_IDS.COMPOSITION;
export const DEFAULT_EXPENSE_MANAGEMENT_SUBTAB_ID = 0;

/** 上タブ切替時に URL へ反映するクエリ（収支専用の category は常に破棄） */
export const syncPropertyDetailTopTabSearchParams = (
  prev: URLSearchParams,
  tab: number,
): URLSearchParams => {
  const next = new URLSearchParams(prev);
  next.set("tab", String(tab));
  next.delete("subtab");
  next.delete("subTab");
  next.delete("category");
  return next;
};

/** 物件詳細 URL で常に許可するクエリ（タブ以外のナビゲーション文脈） */
const PROPERTY_DETAIL_GLOBAL_QUERY_KEYS = ["from"] as const;

export const getAllowedPropertyDetailQueryKeys = (
  tab: number,
): readonly string[] => {
  const base: string[] = ["tab", ...PROPERTY_DETAIL_GLOBAL_QUERY_KEYS];
  if (tab === PROPERTY_DETAIL_TAB_IDS.OVERVIEW) {
    return [...base, "subtab"];
  }
  if (tab === PROPERTY_DETAIL_TAB_IDS.MANAGEMENT) {
    return [...base, "subtab", "category"];
  }
  return base;
};

export const hasUnknownPropertyDetailQueryKeys = (
  searchParams: URLSearchParams,
  tab: number,
): boolean => {
  const allowed = new Set(getAllowedPropertyDetailQueryKeys(tab));
  for (const key of searchParams.keys()) {
    if (!allowed.has(key)) return true;
  }
  return false;
};

/** 収支タブ CardTab の ?category=。未指定 / all は「すべて」扱いで有効。 */
export const isValidExpenseCategoryQuery = (
  categoryParam: string | null,
  validCategoryIds: readonly string[] | undefined,
): boolean => {
  if (categoryParam == null || categoryParam === "") return true;
  if (categoryParam === "all") return true;
  if (validCategoryIds === undefined) return true;
  return validCategoryIds.includes(categoryParam);
};

export const isValidPropertyDetailTabQuery = (
  searchParams: URLSearchParams,
  validExpenseCategoryIds?: readonly string[],
): boolean => {
  if (hasLegacySubTabQuery(searchParams)) return false;

  const tabParam = searchParams.get("tab");
  const subtabParam = searchParams.get("subtab");
  const categoryParam = searchParams.get("category");

  const tab =
    tabParam == null
      ? DEFAULT_PROPERTY_DETAIL_TAB_ID
      : resolveTabId(tabParam, PROPERTY_DETAIL_TABS);
  if (tab == null) return false;

  if (hasUnknownPropertyDetailQueryKeys(searchParams, tab)) return false;

  if (tab === PROPERTY_DETAIL_TAB_IDS.OVERVIEW) {
    if (categoryParam != null) return false;
    return (
      subtabParam == null ||
      resolveTabId(subtabParam, PROPERTY_OVERVIEW_SUBTABS) != null
    );
  }

  if (tab === PROPERTY_DETAIL_TAB_IDS.MANAGEMENT) {
    const subtabOk =
      subtabParam == null ||
      resolveTabId(subtabParam, EXPENSE_MANAGEMENT_SUBTABS) != null;
    if (!subtabOk) return false;
    return isValidExpenseCategoryQuery(
      categoryParam,
      validExpenseCategoryIds,
    );
  }

  return subtabParam == null && categoryParam == null;
};

export { getTabLabel, parseTabId, resolveTabId };
